import { apiGet, apiRoutes, buildApiRoute, normalizePagedResult, toQueryParams } from "./entityApiShared.js";
import type { DropdownOption, EntityRecord, PageQuery } from "../types/api.types.js";
import type { CmtsComplaintDropdowns } from "../core/complaints/cmtsComplaintLifecycleTypes.js";

export type ComplaintDropdownQuery = PageQuery & { readonly parentId?: string | undefined };

export type ComplaintDropdownMasterType =
  | "complaint-source"
  | "complaint-type"
  | "complaint-category"
  | "complaint-subcategory"
  | "severity"
  | "priority"
  | "rca-method"
  | "root-cause-category"
  | "capa-action-type"
  | "capa-status"
  | "verification-method"
  | "verification-rule"
  | "effectiveness-status"
  | "workflow-status"
  | "evidence-type"
  | 'verification-status'
  | "confidentiality"
  | "customers"
  | "suppliers"
  | "external-parties";
  
function isRecord(value: unknown): value is Record<string, unknown> { 
  return typeof value === "object" && value !== null && !Array.isArray(value); 
}

function readString(record: Record<string, unknown>, keys: readonly string[]): string | undefined {
  for (const key of keys) {
    const value = record[key];
    if (typeof value === "string" && value.trim()) return value.trim();
    if (typeof value === "number" && Number.isFinite(value)) return String(value);
  }
  return undefined;
}

function readMetadata(item: Record<string, unknown>): Record<string, unknown> {
  const metadata = item.metadata ?? item.Metadata;
  return isRecord(metadata) ? metadata : {};
}

export function normalizeComplaintDropdownOptions(input: unknown): DropdownOption[] {
  const rows: unknown[] = Array.isArray(input)
    ? input
    : isRecord(input) && Array.isArray(input.data)
      ? input.data
      : isRecord(input) && Array.isArray(input.items)
        ? input.items
        : isRecord(input) &&
            isRecord(input.data) &&
            Array.isArray(input.data.items)
          ? input.data.items
          : isRecord(input) && Array.isArray(input.results)
            ? input.results
            : [];

  return rows
    .map((item): DropdownOption | null => {
      if (!isRecord(item)) return null;

      const backendId = readString(item, ["id", "Id", "value", "Value"]);

      if (!backendId) return null;

      const label =
        readString(item, [
          "label",
          "Label",
          "name",
          "Name",
          "displayName",
          "DisplayName",
          "title",
          "Title",
          "code",
          "Code",
        ]) ?? backendId;

      const code = readString(item, ["code", "Code"]);
      const metadata = readMetadata(item);

      const parentCode =
        readString(item, ["parentCode", "ParentCode"]) ??
        readString(metadata, ["parentCode", "ParentCode", "parentCategoryCode", "ParentCategoryCode"]);

      const parentCategoryId =
        readString(item, [
          "parentCategoryId",
          "ParentCategoryId",
          "parentId",
          "ParentId",
        ]) ??
        readString(metadata, [
          "parentCategoryId",
          "ParentCategoryId",
          "parentId",
          "ParentId",
        ]);

      return {
        id: backendId,
        value: backendId,
        label,
        ...(code ? { code } : {}),
        ...(typeof item.isActive === "boolean"
          ? { isActive: item.isActive }
          : {}),
        metadata: {
          ...metadata,
          id: backendId,
          idDomain: "backend-dropdown-options",
          ...(parentCategoryId ? { parentCategoryId } : {}),
          ...(parentCode ? { parentCode } : {}),
        },
      };
    })
    .filter((option): option is DropdownOption => option !== null);
}
async function dropdown(masterType: ComplaintDropdownMasterType, query?: ComplaintDropdownQuery, signal?: AbortSignal): Promise<DropdownOption[]> {
  const filters = { ...(query?.filters ?? {}) };
  if (query?.parentId) filters.parentId = query.parentId;
  const fullQuery: PageQuery = {
    ...query,
    activeOnly: query?.activeOnly ?? true,
    filters,
  };
  
  const response = await apiGet<unknown>(buildApiRoute(apiRoutes.lifecycleReference.dropdownOptions, { masterType }, { query: toQueryParams(fullQuery) }), { signal });
  
  return normalizeComplaintDropdownOptions(response).filter((option) => option.isActive !== false || query?.activeOnly === false);
}

async function organizationUnits(query?: ComplaintDropdownQuery, signal?: AbortSignal): Promise<DropdownOption[]> {
  const response = await apiGet<unknown>(buildApiRoute(apiRoutes.lifecycleReference.organizationUnits, {}, { query: toQueryParams({ ...query, activeOnly: query?.activeOnly ?? true, pageSize: query?.pageSize ?? 100, filters: { ...(query?.filters ?? {}) } }) }), { signal });
  const rows = normalizePagedResult<EntityRecord>(response, { ...query, pageSize: query?.pageSize ?? 100 }).items;
  const safeRows = Array.isArray(rows)
  ? rows
  : [];
return safeRows.map((row): DropdownOption | null => {
      const backendId = readString(row, ["id", "Id", "organizationUnitId", "OrganizationUnitId", "orgUnitId", "OrgUnitId"]);
    if (!backendId) return null;
    const label = readString(row, ["name", "Name", "orgUnitName", "OrgUnitName", "displayName", "DisplayName", "code", "Code", "orgUnitCode", "OrgUnitCode"]) ?? backendId;
    const code = readString(row, ["code", "Code", "orgUnitCode", "OrgUnitCode"]);
    return { id: backendId, value: backendId, label, ...(code ? { code } : {}), metadata: { id: backendId, idDomain: "organization_units" } };
  }).filter((option): option is DropdownOption => option !== null);
}
async function rcaStatuses(
  query?: PageQuery,
  signal?: AbortSignal,
): Promise<DropdownOption[]> {
  const fullQuery: PageQuery = {
    ...query,
    activeOnly: query?.activeOnly ?? true,
  };

  const response = await apiGet<unknown>(
    buildApiRoute(
      apiRoutes.lifecycleReference.dropdownOptions,
      { masterType: "rca-status" },
      {
        query: toQueryParams(fullQuery),
      },
    ),
    { signal },
  );

  return normalizeComplaintDropdownOptions(response);
}

export const complaintDropdownApi = {
  sources: (query?: ComplaintDropdownQuery, signal?: AbortSignal) => dropdown("complaint-source", query, signal),
  types: (query?: ComplaintDropdownQuery, signal?: AbortSignal) => dropdown("complaint-type", query, signal),
  categories: (query?: ComplaintDropdownQuery, signal?: AbortSignal) => dropdown("complaint-category", query, signal),
     rcaStatuses,
  /**
   * Dedicated single-fetch method for cascading subcategories filtered explicitly by a Category ID
   */
  subcategories: (categoryId?: string, query?: PageQuery, signal?: AbortSignal) => 
    dropdown("complaint-subcategory", { ...query, parentId: categoryId }, signal),

  severities: (query?: ComplaintDropdownQuery, signal?: AbortSignal) => dropdown("severity", query, signal),
  priorities: (query?: ComplaintDropdownQuery, signal?: AbortSignal) => dropdown("priority", query, signal),
  customers: (query?: ComplaintDropdownQuery, signal?: AbortSignal) => dropdown("customers", query, signal),
  suppliers: (query?: ComplaintDropdownQuery, signal?: AbortSignal) => dropdown("suppliers", query, signal),
  externalParties: (query?: ComplaintDropdownQuery, signal?: AbortSignal) => dropdown("external-parties", query, signal),
  master: (masterType: ComplaintDropdownMasterType, query?: ComplaintDropdownQuery, signal?: AbortSignal) => dropdown(masterType, query, signal),
  organizationUnits,
  
  async allForCreate(categoryId?: string, signal?: AbortSignal): Promise<CmtsComplaintDropdowns> {
    const [sourceId, complaintTypeId, categoryIdOptions, subCategoryId, severityId, priorityId, organizationUnitId, customerId, supplierId] = await Promise.all([
      complaintDropdownApi.sources({ activeOnly: true }, signal),
      complaintDropdownApi.types({ activeOnly: true }, signal),
      complaintDropdownApi.categories({ activeOnly: true }, signal),
     complaintDropdownApi.subcategories(categoryId || undefined, { activeOnly: true }, signal).catch(() => []),
      complaintDropdownApi.severities({ activeOnly: true }, signal),
      complaintDropdownApi.priorities({ activeOnly: true }, signal),
      complaintDropdownApi.organizationUnits({ activeOnly: true, pageSize: 100 }, signal),
      complaintDropdownApi.customers({ activeOnly: true }, signal).catch(() => []),
      complaintDropdownApi.suppliers({ activeOnly: true }, signal).catch(() => []),
    ]);
    return {
      sourceId,
      complaintTypeId,
      issueTypeId: complaintTypeId,
      typeId: complaintTypeId,
      categoryId: categoryIdOptions,
      subCategoryId,
      subcategoryId: subCategoryId,
      severityId,
      priorityId,
      organizationUnitId,
      customerId,
      supplierId,
    };
  },
  verificationRules: (query?: ComplaintDropdownQuery, signal?: AbortSignal) => dropdown("verification-rule", query, signal),
  effectivenessStatuses: (query?: ComplaintDropdownQuery, signal?: AbortSignal) => dropdown("effectiveness-status", query, signal),
};
